import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

// True only while JavaScript stays alive (in-app navigation). Resets to false on any
// full page reload or Auth0 redirect, ensuring selections are cleared on refresh/re-login.
let _dashboardSessionActive = false;
import { PowerBIResponse, Department, Institution, Report } from './reportsdashboard.model';
import { ReportsDashboardService } from './reports-dashboard-service';
import { Loader } from '../shared/components/loader/loader';
import { catchError, of, switchMap, tap } from 'rxjs';
import { PageHeader } from '../shared/components/page-header/page-header';
import { NgClass } from '@angular/common';
import { roleAccess } from '../shared/services/state-management.service';

@Component({
  selector: 'app-reports-dashboard',
  imports: [Loader, FormsModule, PageHeader, NgClass],
  templateUrl: './reports-dashboard.html',
})
export class ReportsDashboard implements OnInit {
  private readonly dashboardDepartmentStorageKey = 'dashboard.selectedDepartmentPk';
  private readonly dashboardInstitutionStorageKey = 'dashboard.selectedInstitutionPk';
  private readonly reportsPkStorageKey = 'dashboard.selectedReportPk';

  embedToken: any;
  embedUrl: any;

  accessToken: string = '';

  // Angular 21 Signals
  reports = signal<Report[]>([]);
  selectedReportId = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  showReports = signal(true);
  expandReports = signal(false);
  // Pagination
  currentPage = signal(1);
  pageSize = 10;
  expandLeft = signal(false);
  loadingReports = signal(true);
  embeddingReport = signal(false);
  totalPages = computed(() => Math.max(1, Math.ceil(this.reports().length / this.pageSize)));
  hasSingleReport = computed(() => this.reports().length === 1);
  noReportsFound = computed(() => !this.loadingReports() && this.reports().length === 0);
  isAuthenticated = signal(true);
  institutions = signal<Institution[]>([]);
  userValidationError = signal('');
  departmentLoadError = signal('');
  institutionLoadError = signal('');
  authenticationError = signal('');
  reportsLoadError = signal('');
  reportEmbedError = signal('');
  pageError = computed(() =>
    this.userValidationError() || this.departmentLoadError() || this.institutionLoadError() ||
    this.authenticationError() || this.reportsLoadError()
  );
  paginatedReports = computed(() => {
    const page = this.currentPage();
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.reports().slice(start, start + this.pageSize);
  });
  departments = signal<Department[]>([]);
  selectedDepartment = signal<Department | null>(null);
  selectedInstitution = signal<Institution | null>(null);
  filteredInstitutions = signal<Institution[]>([]);
  activeReportsCount = signal<number>(0);
  reportName: String = '';
  private embeddedReport: any;

  userAccessForReportDashboard: boolean = false;
  constructor(private reportsDashboardService: ReportsDashboardService) { }

  private sortByName<T extends { name?: string | null }>(items: T[]): T[] {
    return [...items].sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, {
        sensitivity: 'base',
      }),
    );
  }

  ngOnInit() {
    const decoded = localStorage.getItem('userRoleAccessToken') ? JSON.parse(localStorage.getItem('userRoleAccessToken') || '') as roleAccess | null : null;
    this.userAccessForReportDashboard = decoded ? decoded?.rolePermissions?.reports.view == true : false;
    this.setReportFullscreenMode(false);
    if (!_dashboardSessionActive) {
      _dashboardSessionActive = true;
      this.persistSelection(null, null, null);
    }
    this.reportsDashboardService
      .getConnections()
      .pipe(
        catchError((error) => {
          console.error('Connections API Error:', error);
          this.authenticationError.set(
            'Unable to load Power BI connections. Please try again later.',
          );
          this.loadingReports.set(false);
          return of(null);
        }),
        tap((response: any) => {
          if (response?.connections?.length > 0) {
            this.reportsDashboardService.setConnectionId(response.connections[0].id);
          }
        }),
      )
      .subscribe(() => {
        this.loadDepartments();
      });
  }

  ngOnDestroy() {
    this.setReportFullscreenMode(false);
  }

  loadDepartments() {
    this.loadingReports.set(true);
    this.reportsDashboardService
      .getDepartments()
      .pipe(
        catchError((error) => {
          console.error('Departments API Error:', error);
          this.departmentLoadError.set(
            'Unable to load departments. Please refresh the page or try again later.',
          );
          this.loadingReports.set(false);
          return of(null);
        }),
      )
      .subscribe((data: any) => {
        if (!data) return;
        const departments = Array.isArray(data?.return_body) ? data.return_body : [];
        const activeDepartments = departments.filter((dept: any) => dept.isLive === true);

        const validatedUser = this.reportsDashboardService.getValidatedUser();
        const superAdminFromToken = this.reportsDashboardService.getSuperAdminFromToken();
        const hasAccessToAllInstitutions = this.reportsDashboardService.hasAccessToAllInstitutionsFromProfile();
        const deptPksFromToken = this.reportsDashboardService.getDepartmentPksFromToken();
        let accessibleDepartments = activeDepartments;

        if (!superAdminFromToken && !hasAccessToAllInstitutions) {
          console.log('Filtering accessible departments for user');
          const departmentValue = validatedUser?.department;
          const userDepartments = Array.isArray(departmentValue)
            ? departmentValue
            : departmentValue != null ? [departmentValue] : [];
          const userDeptIdsFromSession = userDepartments.map((department: any) =>
            String(department?.pk ?? department?.id ?? department).trim(),
          );
          const userDeptIds =
            deptPksFromToken.length > 0 ? deptPksFromToken : userDeptIdsFromSession;
          const userDeptNames = userDepartments.map((department: any) =>
            String(department?.name ?? '').toLowerCase(),
          );
          accessibleDepartments = activeDepartments.filter(
            (dept: any) =>
              userDeptIds.includes(String(dept.pk || dept.id)) ||
              userDeptNames.includes((dept.name || '').toLowerCase()),
          );
        }

        const sortedDepartments = this.sortByName(accessibleDepartments as Department[]);
        this.departments.set(sortedDepartments);
        if (sortedDepartments.length > 0) {
          const storedSelection = this.getStoredSelection();
          const initialDepartment = sortedDepartments.find(
            (dept) => String(dept.pk) === storedSelection.departmentPk,
          ) ?? sortedDepartments[0];

          this.selectedDepartment.set(initialDepartment);
          this.filterInstitutions(initialDepartment.pk, storedSelection.institutionPk);
        }
      });
  }
  comparePk(a: any, b: any): boolean {
    return a && b ? String(a.pk) === String(b.pk) : a === b;
  }

  onDepartmentChange(dept: Department | null) {
    this.isLoading.set(true);
    this.embeddingReport.set(true);
    this.resetEmbeddedReportView();
    this.reports.set([]);
    this.selectedDepartment.set(dept ?? null);

    if (!dept) {
      this.persistSelection(null, null, null);
      this.institutions.set([]);
      this.selectedInstitution.set(null);
      this.reports.set([]);
      this.selectedReportId.set(null);
      this.embeddingReport.set(false);
      this.loadingReports.set(false);
      this.isLoading.set(false);
      return;
    }

    this.persistSelection(String(dept.pk), null, null);
    this.filterInstitutions(dept.pk);
  }

  onInstitutionChange(inst: Institution | null) {
    this.isLoading.set(true);
    this.embeddingReport.set(true);
    this.resetEmbeddedReportView();
    this.reports.set([]);
    this.selectedInstitution.set(inst ?? null);

    this.persistSelection(
      this.selectedDepartment() ? String(this.selectedDepartment()!.pk) : null,
      inst ? String(inst.pk) : null,
      null,
    );

    if (inst) {
      this.loadReport();
    } else {
      this.embeddingReport.set(false);
      this.loadingReports.set(false);
      this.isLoading.set(false);
    }
  }
  loadInstitutions(deptId: number) {
    this.isLoading.set(true);
    this.reportsDashboardService
      .getInstitutions(deptId)
      .pipe(
        catchError((error) => {
          console.error('Institutions API Error:', error);
          this.institutionLoadError.set(
            'Unable to load institutions. Please select a different department or try again later.',
          );
          this.isLoading.set(false);
          return of([]);
        }),
      )
      .subscribe((data: any) => {
        this.institutions.set(data);
        if (data.length) {
          this.selectedInstitution.set(data[0]);
          // Filter based on selected dept
          const dept = this.selectedDepartment();
          if (dept) {
            this.filterInstitutions(dept.pk);
          }
        }
      });
  }
  filterInstitutions(deptId: number, preferredInstitutionPk?: string | null) {
    // Find the selected department object
    const selectedDept = this.departments().find((dept) => String(dept.pk) === String(deptId));
    this.selectedDepartment.set(selectedDept ?? null);
    // Set institutions for the selected department
    if (selectedDept && selectedDept.institutions && selectedDept.institutions.length > 0) {
      const filteredInstitutions = this.sortByName(
        this.getAccessibleInstitutionsForUser(selectedDept.institutions),
      );
      this.institutions.set(filteredInstitutions);

      const preferredPk = preferredInstitutionPk ?? this.getStoredSelection().institutionPk;
      const initialInstitution = filteredInstitutions.find(
        (inst: any) => String(inst.pk) === preferredPk,
      ) ?? filteredInstitutions[0] ?? null;

      this.selectedInstitution.set(initialInstitution);
      this.persistSelection(String(selectedDept.pk), initialInstitution ? String(initialInstitution.pk) : null);

      if (filteredInstitutions.length === 0) {
        this.reports.set([]);
        this.selectedReportId.set(null);
        this.persistSelection(String(selectedDept.pk), null, null);
        this.loadingReports.set(false);
        this.embeddingReport.set(false);
        this.isLoading.set(false);
        return;
      }
      this.loadReport();
    } else {
      this.institutions.set([]);
      this.selectedInstitution.set(null);
      this.persistSelection(this.selectedDepartment() ? String(this.selectedDepartment()!.pk) : null, null, null);
      this.reports.set([]);
      this.loadingReports.set(false);
    }
    this.isLoading.set(false);
  }

  private getStoredSelection(): { departmentPk: string | null; institutionPk: string | null; reportPk: string | null } {
    try {
      return {
        departmentPk: localStorage.getItem(this.dashboardDepartmentStorageKey),
        institutionPk: localStorage.getItem(this.dashboardInstitutionStorageKey),
        reportPk: localStorage.getItem(this.reportsPkStorageKey),
      };
    } catch {
      return { departmentPk: null, institutionPk: null, reportPk: null };
      return { departmentPk: null, institutionPk: null, reportPk: null };
    }
  }


  private persistSelection(
    departmentPk: string | null,
    institutionPk: string | null,
    reportPk: string | null = this.getStoredSelection().reportPk,
  ): void {
    try {
      if (departmentPk) {
        localStorage.setItem(this.dashboardDepartmentStorageKey, departmentPk);
      } else {
        localStorage.removeItem(this.dashboardDepartmentStorageKey);
      }

      if (institutionPk) {
        localStorage.setItem(this.dashboardInstitutionStorageKey, institutionPk);
      } else {
        localStorage.removeItem(this.dashboardInstitutionStorageKey);
      }

      if (reportPk) {
        localStorage.setItem(this.reportsPkStorageKey, reportPk);
      } else {
        localStorage.removeItem(this.reportsPkStorageKey);
      }
    } catch {
      // Ignore storage access issues (e.g., private browsing restrictions).
    }
  }
  loadReport() {
    this.isLoading.set(true);
    this.loadingReports.set(true);
    const instPk = String(this.selectedInstitution()?.pk ?? '');
    const validatedUser = this.reportsDashboardService.getValidatedUser();
    const userId = validatedUser?.email || '';

    if (!instPk || !userId) {
      this.reports.set([]);
      this.selectedReportId.set(null);
      this.loadingReports.set(false);
      this.isLoading.set(false);
      return;
    }

    this.reportsDashboardService
      .getReports(this.accessToken, instPk, userId)
      .pipe(
        catchError((error) => {
          console.error('Reports API Error:', error);
          this.reportsLoadError.set(
            'Unable to fetch the reports list. Please check your connection and try again.',
          );
          this.loadingReports.set(false);
          this.isLoading.set(false);
          return of(null);
        }),
      )
      .subscribe((reports: any) => {
        if (!reports) {
          this.embeddingReport.set(false);
          this.loadingReports.set(false);
          this.isLoading.set(false);
          return;
        }
        const validatedUser = this.reportsDashboardService.getJwtPayload();
        const isSuperAdmin = validatedUser.superAdmin; // or your actual property
        const isExternalUser = validatedUser.userType === 'external';
        let reportList = (reports.reports || []).sort((a: any, b: any) =>
          String(a?.reportName ?? '').localeCompare(String(b?.reportName ?? '')),
        );
        // Hide internal reports for external users who are not Super Admins
        if (isExternalUser && !isSuperAdmin) {
          reportList = reportList.filter((report: any) => !report.internalReport);
        }
        this.activeReportsCount.set(reportList.filter((report: { active: any; }) => report.active).length);
        this.reports.set(reportList);
        this.loadingReports.set(false);
        this.isLoading.set(false);
        if (reportList.length > 0) {
          const storedReportPk = this.getStoredSelection().reportPk;
          const reportToSelect = reportList.find(
            (report: any) => String(report.pk) === storedReportPk,
          ) ?? reportList[0];
          const reportIndex = reportList.indexOf(reportToSelect);
          this.currentPage.set(reportIndex >= 0 ? Math.floor(reportIndex / this.pageSize) + 1 : 1);
          this.selectReport(reportToSelect);
        } else {
          this.persistSelection(
            this.selectedDepartment() ? String(this.selectedDepartment()!.pk) : null,
            this.selectedInstitution() ? String(this.selectedInstitution()!.pk) : null,
            null,
          );
          this.embeddingReport.set(false);
        }
      });
  }

  selectReport(report: any) {
    this.expandLeft.set(false);
    this.selectedReportId.set(report.pk);
    this.reportEmbedError.set('');
    this.embeddingReport.set(true);
    this.persistSelection(
      this.selectedDepartment() ? String(this.selectedDepartment()!.pk) : null,
      this.selectedInstitution() ? String(this.selectedInstitution()!.pk) : null,
      String(report.pk),
    );
    const instPk = String(this.selectedInstitution()?.pk ?? '');
    this.reportsDashboardService
      .getEmbedToken(report.pk, instPk, report.pbiDataset) // Pass role to getEmbedToken
      .pipe(
        catchError((error) => {
          this.reportName = report.reportName;
          console.error('Embed Token API Error:', error);
          this.reportEmbedError.set('Unable to embed "' + (report.reportName || 'this report') + '". The report may be unavailable or misconfigured.');
          this.embeddingReport.set(false);
          return of(null);
        }),
      )
      .subscribe((response: PowerBIResponse | null) => {
        if (!response) {
          this.embeddingReport.set(false);
          return;
        }
        this.embedToken = response.embedToken;
        this.reportName = report.reportName;
        this.embedUrl = response.embedUrl;
        this.embedReport(report);
        this.embeddingReport.set(false);
      });
  }

  // Pagination
  paginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.reports().slice(start, start + this.pageSize);
  });

  firstPage() {
    if (this.currentPage() !== 1) {
      this.currentPage.set(1);
      this.selectFirstOnPage();
    }
  }

  prevPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
      this.selectFirstOnPage();
    }
  }

  nextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
      this.selectFirstOnPage();
    }
  }

  lastPage() {
    if (this.currentPage() !== this.totalPages()) {
      this.currentPage.set(this.totalPages());
      this.selectFirstOnPage();
    }
  }

  private selectFirstOnPage() {
    const pageReports = this.paginatedReports();
    if (pageReports.length > 0) {
      this.selectReport(pageReports[0]);
    }
  }
  clearPageError() {
    this.userValidationError.set('');
    this.departmentLoadError.set('');
    this.institutionLoadError.set('');
    this.authenticationError.set('');
    this.reportsLoadError.set('');
  }
  toggleReports() {
    this.showReports.update((v: any) => !v);
  }

  toggleExpand() {
    this.expandReports.update((v: any) => !v);
    this.setReportFullscreenMode(this.expandReports());
  }
  toggleLeftExpand() {
    this.expandLeft.set(!this.expandLeft());
  }

  private getAccessibleInstitutionsForUser(institutions: Institution[]): Institution[] {
    const superAdminFromToken = this.reportsDashboardService.getSuperAdminFromToken();
    const hasAccessToAllInstitutions = this.reportsDashboardService.hasAccessToAllInstitutionsFromProfile();
    if (superAdminFromToken || hasAccessToAllInstitutions) {
      return institutions;
    }

    const allowedInstitutionPks = this.reportsDashboardService.getAllowedInstitutionPksFromToken();
    if (allowedInstitutionPks.length) {
      const allowedSet = new Set(allowedInstitutionPks.map((pk) => String(pk).trim()));
      return institutions.filter((inst: any) =>
        allowedSet.has(String(inst.pk ?? inst.id).trim()),
      );
    }

    const validatedUser = this.reportsDashboardService.getValidatedUser();
    if (!validatedUser) {
      return [];
    }

    const userInstitutions = validatedUser.institutions || validatedUser.institution || [];

    if (!Array.isArray(userInstitutions) || !userInstitutions.length) {
      return [];
    }

    const allowedInstitutionIds = new Set(
      userInstitutions
        .map((institution: any) => String(institution?.pk ?? institution?.id ?? institution ?? '').trim())
        .filter(Boolean),
    );

    return institutions.filter((inst: any) =>
      allowedInstitutionIds.has(String(inst.pk ?? inst.id).trim()),
    );
  }

  private embedReport(report: any) {
    const models = (window as any)['powerbi-client'].models;
    const displayOptionMap: Record<string, number> = {
      fitToPage: models.DisplayOption.FitToPage,
      fitToWidth: models.DisplayOption.FitToWidth,
      actualSize: models.DisplayOption.ActualSize,
    };
    const displayOption = displayOptionMap[report.pageView] ?? models.DisplayOption.FitToPage;
    const embedConfig = {
      type: 'report',
      tokenType: models.TokenType.Embed,
      accessToken: this.embedToken,
      embedUrl: this.embedUrl,
      id: report.id,
      permissions: models.Permissions.All,
      settings: {
        filterPaneEnabled: report.filterPaneEnabled,
        navContentPaneEnabled: report.navContentPaneEnabled,
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: displayOption,
        },
      },
    };

    const reportContainer = document.getElementById('reportContainer');
    (window as any)['powerbi'].reset(reportContainer);
    this.embeddedReport = (window as any)['powerbi'].embed(reportContainer, embedConfig);
    const reportTitle = `${String(report?.reportName ?? 'Power BI report')} viewer`;
    // Power BI injects iframe asynchronously; set accessible name once rendered.
    this.embeddedReport?.on?.('loaded', async () => {
      try {
        const pages = await this.embeddedReport.getPages();
        // Match by display name
        const targetPage = pages.find(
          (page: any) => page.displayName === report.pageName
        );

        if (targetPage) {
          await targetPage.setActive();
        } else {
          console.warn('Page not found:', report.pageName);
        }
        this.setEmbeddedIframeAccessibility(reportTitle);
      } catch (error) {
        console.error('Error setting embedded iframe accessibility:', error);
      }
    });
    setTimeout(() => this.setEmbeddedIframeAccessibility(reportTitle), 0);
  }
  private setEmbeddedIframeAccessibility(title: string) {
    const reportContainer = document.getElementById('reportContainer');
    const iframe = reportContainer?.querySelector('iframe');

    if (!iframe) {
      return;
    }

    iframe.setAttribute('title', title);
    iframe.setAttribute('aria-label', title);
  }

  private resetEmbeddedReportView() {
    this.selectedReportId.set(null);
    this.embedToken = null;
    this.embedUrl = null;
    this.reportEmbedError.set('');

    const reportContainer = document.getElementById('reportContainer');
    if (reportContainer && (window as any)['powerbi']) {
      (window as any)['powerbi'].reset(reportContainer);
    }
  }

  private setReportFullscreenMode(isEnabled: boolean) {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.classList.toggle('report-viewer-fullscreen', isEnabled);
  }
}